import { MediaRecorderEncoderSink, isWebmMediaRecorderSupported } from '@/rendering/export/adapters/MediaRecorderEncoderSink'
import {
  WebCodecsMp4EncoderSink,
  probeWebCodecsMp4Export,
} from '@/rendering/export/adapters/WebCodecsMp4EncoderSink'
import {
  WebCodecsWebmEncoderSink,
  probeWebCodecsWebmExport,
} from '@/rendering/export/adapters/WebCodecsWebmEncoderSink'
import {
  videoContainerMeta,
  type VideoExportFormatId,
} from '@/rendering/export/exportPresets'
import type { EncoderFrameSink, ExportRenderQualityId } from '@/rendering/export/types'

export type ResolvedVideoEncoder = {
  sink: EncoderFrameSink
  format: VideoExportFormatId
  mime: string
  extension: 'mp4' | 'webm'
  pipeline:
    | 'webcodecs-avc-mp4'
    | 'webcodecs-vp9-webm'
    | 'webcodecs-vp9-webm-alpha'
    | 'mediarecorder-webm'
  alpha: boolean
}

export type ResolveVideoEncoderParams = {
  format: VideoExportFormatId
  width: number
  height: number
  fps: number
  quality: ExportRenderQualityId
  alpha?: boolean
}

export type VideoExportUnsupportedCode =
  | 'generic'
  | 'transparent-requires-webm'
  | 'vp9-alpha-unavailable'
  | 'mp4-unsupported'
  | 'webm-unsupported'
  | 'no-codec-path'

export class VideoExportNotSupportedError extends Error {
  readonly code: VideoExportUnsupportedCode

  constructor(message: string, code: VideoExportUnsupportedCode = 'generic') {
    super(message)
    this.name = 'VideoExportNotSupportedError'
    this.code = code
  }
}

function logVideoExport(message: string, extra?: Record<string, unknown>): void {
  if (!import.meta.env.DEV) return
  if (extra) {
    console.info(`[VideoExport] ${message}`, extra)
    return
  }
  console.info(`[VideoExport] ${message}`)
}

export async function resolveVideoEncoder(
  params: ResolveVideoEncoderParams,
): Promise<ResolvedVideoEncoder> {
  const alpha = params.alpha === true
  logVideoExport('Resolving video encoder', {
    format: params.format,
    width: params.width,
    height: params.height,
    fps: params.fps,
    quality: params.quality,
    alpha,
  })

  if (alpha) {
    if (params.format !== 'webm') {
      throw new VideoExportNotSupportedError(
        'Transparent backgrounds can only be exported as WebM (VP9 + alpha). Switch the format to WebM and try again.',
        'transparent-requires-webm',
      )
    }
    const alphaProbe = await probeWebCodecsWebmExport({ ...params, alpha: true })
    if (!alphaProbe) {
      throw new VideoExportNotSupportedError(
        'This browser does not support WebM VP9 with alpha. Turn off transparent background or try Chrome / Edge again.',
        'vp9-alpha-unavailable',
      )
    }
    const meta = videoContainerMeta('webm')
    return {
      sink: new WebCodecsWebmEncoderSink({ alpha: true }),
      format: 'webm',
      mime: meta.mime,
      extension: meta.extension,
      pipeline: 'webcodecs-vp9-webm-alpha',
      alpha: true,
    }
  }

  if (params.format === 'mp4') {
    const mp4Supported = await probeWebCodecsMp4Export(params)
    if (!mp4Supported) {
      logVideoExport('MP4 probe failed', {
        format: params.format,
        width: params.width,
        height: params.height,
        fps: params.fps,
      })
      throw new VideoExportNotSupportedError(
        'The current browser environment does not support MP4 export at this resolution. Try exporting 1K as MP4, keep 2K and switch to WebM, or retry in the latest Chrome / Edge.',
        'mp4-unsupported',
      )
    }

    const meta = videoContainerMeta('mp4')
    return {
      sink: new WebCodecsMp4EncoderSink(),
      format: 'mp4',
      mime: meta.mime,
      extension: meta.extension,
      pipeline: 'webcodecs-avc-mp4',
      alpha: false,
    }
  }

  if (await probeWebCodecsWebmExport(params)) {
    const meta = videoContainerMeta('webm')
    return {
      sink: new WebCodecsWebmEncoderSink(),
      format: 'webm',
      mime: meta.mime,
      extension: meta.extension,
      pipeline: 'webcodecs-vp9-webm',
      alpha: false,
    }
  }

  if (isWebmMediaRecorderSupported()) {
    const meta = videoContainerMeta('webm')
    return {
      sink: new MediaRecorderEncoderSink(),
      format: 'webm',
      mime: meta.mime,
      extension: meta.extension,
      pipeline: 'mediarecorder-webm',
      alpha: false,
    }
  }

  throw new VideoExportNotSupportedError(
    'This browser does not support video export in the current environment.',
    params.format === 'webm' ? 'webm-unsupported' : 'no-codec-path',
  )
}
